import {
  IChangeUsefulMessageVote,
  IConfirmResolution,
  ICreateTicket,
  IDeleteMessage,
  IModeratedTicket,
  ISendTicketMessage,
  ISetTicketReview,
  ITicket,
  IUpdateAssignee
} from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { sdk } from '@/services';

const TicketService = sdk.getService('TicketService');

export const useMutateMyTickets = () => {
  const { t } = useTranslation(['CommonTicketsTranslations', 'StudentTicketsTranslations']);

  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: ['tickets'] });

  const { mutate: updateTicket, isPending: isUpdating } = useMutation({
    mutationFn: (ticket: Partial<ITicket>) => TicketService.updateTicket(ticket),
    onSuccess
  });

  const { mutate: setTicketAsResolved, isPending: isResolving } = useMutation({
    mutationFn: (ticketId: string) => TicketService.setTicketAsResolved(ticketId),
    onSuccess
  });

  const { mutate: removeTicket, isPending: isRemoving } = useMutation({
    mutationFn: (ticketId: string) => TicketService.removeTicket(ticketId),
    onSuccess: () => {
      onSuccess();
      message.success(t('CommonTicketsTranslations:TICKET_REMOVED_SUCCESSFULLY'));
    }
  });

  const { mutate: sendMessage, isPending: isSending } = useMutation({
    mutationFn: (sendTicketMessage: ISendTicketMessage) =>
      TicketService.sendMessage(sendTicketMessage),
    onSuccess: ({ canCreateTicket }: IModeratedTicket) => {
      if (!canCreateTicket) return;
      onSuccess();
      message.success(t('CommonTicketsTranslations:TICKET_MESSAGE_SENT_SUCCESSFULLY'));
    }
  });

  const { mutate: setTicketReview, isPending: isSettingReview } = useMutation({
    mutationFn: (ticketReview: ISetTicketReview) => TicketService.setTicketReview(ticketReview),
    onSuccess
  });

  const { mutate: updateTicketAssignee, isPending: isUpdatingAssignee } = useMutation({
    mutationFn: (updateAssignee: IUpdateAssignee) =>
      TicketService.updateTicketAssignee(updateAssignee),
    onSuccess
  });

  const { mutate: changeUsefulTicketVote, isPending: isChangingUsefulTicketVote } = useMutation({
    mutationFn: (ticketId: string) => TicketService.changeUsefulTicketVote(ticketId),
    onSuccess
  });

  const { mutate: deleteMessage, isPending: isDeletingMessage } = useMutation({
    mutationFn: (m: IDeleteMessage) => TicketService.deleteMessage(m),
    onSuccess: () => {
      onSuccess();
      message.success(t('CommonTicketsTranslations:TICKET_MESSAGE_DELETED_SUCCESSFULLY'));
    }
  });

  const { mutate: createTicket, isPending: isCreatingTicket } = useMutation({
    mutationFn: (ticket: ICreateTicket) => TicketService.createTicket(ticket),
    onSuccess: ({ canCreateTicket }: IModeratedTicket) => {
      if (!canCreateTicket) return;
      onSuccess();
      message.success(t('CommonTicketsTranslations:TICKET_CREATED_SUCCESSFULLY'));
    }
  });

  const { mutate: changeUsefulMessageVote, isPending: isChangingUsefulMessageVote } = useMutation({
    mutationFn: (changeMessageVote: IChangeUsefulMessageVote) =>
      TicketService.changeUsefulMessageVote(changeMessageVote),
    onSuccess
  });

  const { mutate: confirmResolution, isPending: isConfirmingResolution } = useMutation({
    mutationFn: (data: IConfirmResolution) => TicketService.confirmResolution(data),
    onSuccess
  });

  return {
    updateTicket,
    removeTicket,
    sendMessage,
    setTicketReview,
    updateTicketAssignee,
    changeUsefulTicketVote,
    setTicketAsResolved,
    deleteMessage,
    createTicket,
    changeUsefulMessageVote,
    confirmResolution,
    isWorking:
      isUpdating ||
      isRemoving ||
      isSending ||
      isSettingReview ||
      isUpdatingAssignee ||
      isChangingUsefulTicketVote ||
      isResolving ||
      isDeletingMessage ||
      isCreatingTicket ||
      isChangingUsefulMessageVote ||
      isConfirmingResolution
  };
};
